import axios from 'axios';

const DEFAULT_API_ERROR_MESSAGE = 'Ha ocurrido un error de comunicacion con la API.';

export const DEFAULT_API_BASE_URL = 'http://localhost:3000/api/v1';
export const DEFAULT_API_TIMEOUT = 15000;
export const EXPEDITION_BILLING_APPLICATION_CODE = 'EXPEDITION_BILLING';
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL?.trim() || DEFAULT_API_BASE_URL;
export const API_TIMEOUT = Number.parseInt(
  process.env.REACT_APP_API_TIMEOUT || `${DEFAULT_API_TIMEOUT}`,
  10
);

let redirectingToLogin = false;

export function buildQueryParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;
      return true;
    })
  );
}

export function buildApiHeaders({ applicationCode, headers = {} } = {}) {
  const resolvedHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...headers,
  };

  if (applicationCode) resolvedHeaders['x-application-code'] = applicationCode;
  return resolvedHeaders;
}

export function getApiErrorMessage(error, fallbackMessage = DEFAULT_API_ERROR_MESSAGE) {
  const data = error?.response?.data;
  return (
    (typeof data === 'string' && data) ||
    data?.message ||
    data?.error ||
    error?.message ||
    fallbackMessage
  );
}

export function redirectToLogin() {
  if (
    typeof window === 'undefined' ||
    window.location.pathname === '/login' ||
    redirectingToLogin
  ) return;

  redirectingToLogin = true;
  window.location.assign('/login');
}

export function createApiClient({
  applicationCode,
  baseURL = API_BASE_URL,
  timeout = API_TIMEOUT,
  headers,
} = {}) {
  const instance = axios.create({
    baseURL,
    timeout,
    withCredentials: true,
    headers: buildApiHeaders({ applicationCode, headers }),
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const request = error?.config;
      const path = String(request?.url || '');
      const isAuthRequest = path.startsWith('/auth/login') || path.startsWith('/auth/refresh');

      if (error?.response?.status === 401 && request && !request._retry && !isAuthRequest) {
        request._retry = true;
        try {
          await authApiClient.post('/auth/refresh');
          return instance(request);
        } catch {
          redirectToLogin();
        }
      } else if (error?.response?.status === 401 && !isAuthRequest) {
        redirectToLogin();
      }

      error.message = getApiErrorMessage(error);
      return Promise.reject(error);
    }
  );

  return instance;
}

function unwrapResponseData(response) {
  return response?.data;
}

function getPaginated(client, path, params = {}) {
  return client.get(path, { params: buildQueryParams(params) }).then(unwrapResponseData);
}

function postJson(client, path, payload = {}) {
  return client.post(path, payload).then(unwrapResponseData);
}

function patchJson(client, path, payload = {}) {
  return client.patch(path, payload).then(unwrapResponseData);
}

function putJson(client, path, payload = {}) {
  return client.put(path, payload).then(unwrapResponseData);
}

function deleteRequest(client, path) {
  return client.delete(path).then(unwrapResponseData);
}

export const authApiClient = createApiClient();
export const expeditionBillingApiClient = createApiClient({
  applicationCode: EXPEDITION_BILLING_APPLICATION_CODE,
});

export function buildLoginPayload(credentials = {}) {
  return {
    login: credentials?.login ?? credentials?.username ?? '',
    password: credentials?.password ?? '',
  };
}

export function login(credentials) {
  return authApiClient.post('/auth/login', buildLoginPayload(credentials)).then(unwrapResponseData);
}

export function getCurrentUser() {
  return authApiClient.get('/auth/me').then(unwrapResponseData);
}

export function logout() {
  return authApiClient.post('/auth/logout').then(unwrapResponseData);
}

export function getExpeditionBillingPendingCustomers(params = {}) {
  return getPaginated(expeditionBillingApiClient, '/expedition-billing/pending/customers', params);
}

export function synchronizeExpeditionBillingPending() {
  return postJson(expeditionBillingApiClient, '/expedition-billing/pending/synchronize', {});
}

export function getExpeditionBillingPendingSynchronization(executionId) {
  return expeditionBillingApiClient
    .get(`/expedition-billing/pending/synchronizations/${executionId}`)
    .then(unwrapResponseData);
}

export function getExpeditionBillingModels() {
  return expeditionBillingApiClient.get('/expedition-billing/models').then(unwrapResponseData);
}

export function createExpeditionBillingModel(payload) {
  return postJson(expeditionBillingApiClient, '/expedition-billing/models', payload);
}

export function updateExpeditionBillingModel(id, payload) {
  return patchJson(expeditionBillingApiClient, `/expedition-billing/models/${id}`, payload);
}

export function getExpeditionBillingCustomerConfigurations(params = {}) {
  return getPaginated(expeditionBillingApiClient, '/expedition-billing/customer-configurations', params);
}

export function updateExpeditionBillingCustomerConfiguration(customerCode, payload) {
  return putJson(
    expeditionBillingApiClient,
    `/expedition-billing/customer-configurations/${encodeURIComponent(customerCode)}`,
    payload
  );
}

export function getExpeditionBillingProcessStatuses() {
  return expeditionBillingApiClient.get('/expedition-billing/process-statuses').then(unwrapResponseData);
}

export function getExpeditionBillingPendingPedidos(params = {}) {
  return getPaginated(expeditionBillingApiClient, '/expedition-billing/pending/pedidos', params);
}

export function getExpeditionBillingPendingPedidoDetails(params = {}) {
  return getPaginated(expeditionBillingApiClient, '/expedition-billing/pending/pedidos/details', params);
}

export function createExpeditionBillingProcess(payload) {
  return postJson(expeditionBillingApiClient, '/expedition-billing/processes', payload);
}

export function getExpeditionBillingProcesses(params = {}) {
  return getPaginated(expeditionBillingApiClient, '/expedition-billing/processes', params);
}

export function deleteExpeditionBillingProcess(id) {
  return deleteRequest(expeditionBillingApiClient, `/expedition-billing/processes/${id}`);
}

export function getExpeditionBillingProcess(id) {
  return expeditionBillingApiClient.get(`/expedition-billing/processes/${id}`).then(unwrapResponseData);
}

export function getExpeditionBillingProcessLines(id, params = {}) {
  return getPaginated(expeditionBillingApiClient, `/expedition-billing/processes/${id}/lines`, params);
}

export function getExpeditionBillingProposalCandidates(id) {
  return expeditionBillingApiClient.get(`/expedition-billing/processes/${id}/proposal-candidates`).then(unwrapResponseData);
}

export function getExpeditionBillingLiquidations(processId) {
  return expeditionBillingApiClient
    .get(`/expedition-billing/processes/${processId}/liquidations`)
    .then(unwrapResponseData);
}

export function downloadExpeditionBillingLiquidation(processId, liquidationId) {
  return expeditionBillingApiClient
    .get(`/expedition-billing/processes/${processId}/liquidations/${liquidationId}/download`, { responseType: 'blob' })
    .then(unwrapResponseData);
}

export function uploadExpeditionBillingLiquidation(processId, file) {
  const formData = new FormData();
  formData.append('file', file);
  return expeditionBillingApiClient
    .post(`/expedition-billing/processes/${processId}/liquidations`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then(unwrapResponseData);
}

export function retryExpeditionBillingLiquidation(id) {
  return postJson(expeditionBillingApiClient, `/expedition-billing/liquidations/${id}/retry`);
}

export function getExpeditionBillingProposal(processId) {
  return expeditionBillingApiClient
    .get(`/expedition-billing/processes/${processId}/proposal`)
    .then(unwrapResponseData);
}

export function generateExpeditionBillingProposal(processId) {
  return postJson(expeditionBillingApiClient, `/expedition-billing/processes/${processId}/proposal/generate`);
}

export function createExpeditionBillingInvoicesInApplication(processId, payload = {}) {
  return postJson(expeditionBillingApiClient, `/expedition-billing/processes/${processId}/create-invoices`, payload);
}

export function sendExpeditionBillingInvoicesToBusinessCentral(processId) {
  return expeditionBillingApiClient
    .post(`/expedition-billing/processes/${processId}/send-invoices-to-business-central`, {}, { timeout: 120000 })
    .then(unwrapResponseData);
}

export function resendExpeditionBillingInvoiceToBusinessCentral(processId, invoiceId) {
  return expeditionBillingApiClient
    .post(`/expedition-billing/processes/${processId}/invoices/${invoiceId}/resend-to-business-central`, {}, { timeout: 120000 })
    .then(unwrapResponseData);
}

export function revertExpeditionBillingInvoiceCreation(processId) {
  return postJson(expeditionBillingApiClient, `/expedition-billing/processes/${processId}/revert-invoice-creation`);
}

export function updateExpeditionBillingProposalLineAllocations(proposalLineId, payload) {
  return patchJson(expeditionBillingApiClient, `/expedition-billing/proposal-lines/${proposalLineId}/allocations`, payload);
}

export function updateExpeditionBillingProposalLinePrice(proposalLineId, payload) {
  return patchJson(expeditionBillingApiClient, `/expedition-billing/proposal-lines/${proposalLineId}/price`, payload);
}

export function updateExpeditionBillingProposalLinePriceUnit(proposalLineId, payload) {
  return patchJson(expeditionBillingApiClient, `/expedition-billing/proposal-lines/${proposalLineId}/price-unit`, payload);
}

export function updateExpeditionBillingProposalLineManualPrice(proposalLineId, payload) {
  return patchJson(expeditionBillingApiClient, `/expedition-billing/proposal-lines/${proposalLineId}/manual-price`, payload);
}

export function getExpeditionBillingMappingCustomers() {
  return expeditionBillingApiClient.get('/expedition-billing/mappings/customers').then(unwrapResponseData);
}

export function getExpeditionBillingProductAttributeValues(params = {}) {
  return expeditionBillingApiClient.get('/expedition-billing/mappings/product-attribute-values', { params }).then(unwrapResponseData);
}

export function getExpeditionBillingCustomerDestinations(params = {}) {
  return expeditionBillingApiClient.get('/expedition-billing/mappings/destinations', { params }).then(unwrapResponseData);
}

export function getExpeditionBillingProductAttributeMappings(params = {}) {
  return getPaginated(expeditionBillingApiClient, '/expedition-billing/mappings/product-attributes', params);
}

export function createExpeditionBillingProductAttributeMapping(payload) {
  return postJson(expeditionBillingApiClient, '/expedition-billing/mappings/product-attributes', payload);
}

export function updateExpeditionBillingProductAttributeMapping(id, payload) {
  return patchJson(expeditionBillingApiClient, `/expedition-billing/mappings/product-attributes/${id}`, payload);
}

export function getExpeditionBillingDestinationMappings(params = {}) {
  return getPaginated(expeditionBillingApiClient, '/expedition-billing/mappings/destination-values', params);
}

export function createExpeditionBillingDestinationMapping(payload) {
  return postJson(expeditionBillingApiClient, '/expedition-billing/mappings/destination-values', payload);
}

export function updateExpeditionBillingDestinationMapping(id, payload) {
  return patchJson(expeditionBillingApiClient, `/expedition-billing/mappings/destination-values/${id}`, payload);
}

export function syncExpeditionBillingMasterData() {
  return postJson(expeditionBillingApiClient, '/expedition-billing/master-data/synchronize', {});
}

export function getExpeditionBillingMasterDataSyncExecution(executionId) {
  return expeditionBillingApiClient
    .get(`/expedition-billing/master-data/synchronizations/${executionId}`)
    .then(unwrapResponseData);
}
